import { Component, Inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import {
  Collaborator,
  CollaboratorStatus,
  ClusterCollaboratorFragment,
  ClusterCollaboratorsGQL,
  ClusterRevokeIviteGQL,
  ClusterDeleteCollaboratorGQL, InviteCollaboratorGQL,
} from "@/core/graphql/graphql";
import { EMPTY } from 'rxjs';
import { ConfirmationModalService } from '@airnauts/ng-components/confirmation-modal-content';
import { filter, first, switchMap, tap } from 'rxjs/operators';
import {
  COLLABORATORS_COLUMNS,
} from "@/modules/clusters/components/cluster-details-modal/components/cluster-collaborators/cluster-collaborators.const";
import {
  ClusterCollaboratorsDatasource,
} from "@/modules/clusters/components/cluster-details-modal/components/cluster-collaborators/cluster-collaborators.datasource";
import { Column } from "@/shared/components/table/table.model";
import { MAT_DIALOG_DATA } from "@angular/material/dialog";
import { ConfirmDialogComponent } from "@/shared/components/confirm-dialog/confirm-dialog.component";
import { CosmosDialogService, CosmosDialogStyle } from "@/core/services/cosmos-dialog.service";
import { ConfirmDialogModel } from "@/shared/components/confirm-dialog/confirm-dialog.model";
import {
  ClusterAddCollaboratorComponent,
} from "@/modules/clusters/components/cluster-details-modal/components/cluster-collaborators/modal/cluster-add-collaborator/cluster-add-collaborator.component";
import { UserDetailsModalComponent } from "@/modules/users/components/user-details-modal/user-details-modal.component";

@Component({
  selector: 'cosmos-cluster-collaborators',
  templateUrl: './cluster-collaborators.component.html',
  styleUrls: ['./cluster-collaborators.component.scss'],
})
export class ClusterCollaboratorsComponent implements OnInit {
  public _columns: Column<ClusterCollaboratorFragment>[] = [];
  public _dataSource: ClusterCollaboratorsDatasource;

  @ViewChild('actions', { static: true }) private _actions: TemplateRef<any>;

  public constructor(
    private readonly _confirmationModalService: ConfirmationModalService,
    private readonly _clusterCollaboratorsGql: ClusterCollaboratorsGQL,
    private readonly _clusterRevokeInviteGql: ClusterRevokeIviteGQL,
    private readonly _clusterDeleteCollaboratorGql: ClusterDeleteCollaboratorGQL,
    private readonly _dialog: CosmosDialogService,
    private readonly _inviteCollaboratorGql: InviteCollaboratorGQL,
    @Inject(MAT_DIALOG_DATA) private readonly _data: { elementId: number },
  ) {
    this._dataSource = new ClusterCollaboratorsDatasource(_clusterCollaboratorsGql, this._data.elementId);
  }

  public ngOnInit(): void {
    this._columns = COLLABORATORS_COLUMNS(this._actions);
    this._dataSource.loadData();
  }

  public openUserDetails(row: ClusterCollaboratorFragment): void {
    this._dialog.open<UserDetailsModalComponent, { userId: number }>(
      UserDetailsModalComponent,
      {
        data: {
          userId: row.userId,
        },
        style: CosmosDialogStyle.Dark,
      });
  }

  public openAddCollaboratorDialog(): void {
    const dialogRef = this._dialog.open<ClusterAddCollaboratorComponent>(ClusterAddCollaboratorComponent, {
      style: CosmosDialogStyle.Gray,
    });

    dialogRef.afterClosed().pipe(
      first(),
      filter(data => !!data),
      switchMap((userId) => this._inviteCollaboratorGql.mutate({
        clusterId: this._data.elementId,
        userId: Number(userId),
      }),
      ),
      tap((_) => this._dataSource.loadData()),
    ).subscribe();
  }

  public removeCollaborator(row: Collaborator): void {
    const dialogRef = this._dialog.open<ConfirmDialogComponent, ConfirmDialogModel>(
      ConfirmDialogComponent,
      {
        data: {
          dialogMessage: `Would you like to delete this collaborator (id ${row.userId})?`,
          confirmText: 'Confirm',
          cancelText: 'Cancel',
        },
        style: CosmosDialogStyle.Gray,
      });

    dialogRef.afterClosed().pipe(
      first(),
      filter(data => !!data),
      switchMap(() => {
        if (row.status === CollaboratorStatus.Accepted) {
          return this._clusterDeleteCollaboratorGql.mutate({
            clusterId: this._data.elementId,
            userId: row.userId,
          });
        } else if (row.status === CollaboratorStatus.Invited) {
          return this._clusterRevokeInviteGql.mutate({
            clusterId: this._data.elementId,
            userId: row.userId,
          });
        }
        return EMPTY;
      }),
      tap((_) => this._dataSource.loadData()),
    ).subscribe();
  }
}
